import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Sparkles, FileText, Send, Clock, Check, ChevronDown,
  Zap, Shield, Wallet, Smartphone, Users, BarChart3, Receipt, Star
} from "lucide-react";
import InteractiveDemo from "@/components/InteractiveDemo";
import { track } from "@/lib/analytics";
import { LegalLinks, PrivacyCheckbox } from "@/components/LegalLinks";
import useLegalInfo from "@/hooks/useLegalInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRADES = ["Reformas", "Pintura", "Electricidad", "Fontanería", "Carpintería", "Limpieza", "Jardinería", "Instalaciones"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Nav />
      <Hero />
      <SocialProof />
      <Features />
      <DemoVideo />
      <Trades />
      <Testimonials />
      <Pricing />
      <FAQ />
      <FinalCta />
      <Footer />
    </div>
  );
}

/* ---------------- Nav ---------------- */
function Nav() {
  return (
    <header className="border-b border-slate-200 sticky top-0 bg-white/90 backdrop-blur z-40">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="font-display font-extrabold text-xl tracking-tight" data-testid="nav-logo">
          AutoPresupuestos<span className="text-orange-600">.</span>
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2 text-sm">
          <a href="#como-funciona" className="hidden sm:inline-flex px-3 h-10 items-center hover:text-orange-600 font-semibold">Cómo funciona</a>
          <a href="#precio" className="hidden sm:inline-flex px-3 h-10 items-center hover:text-orange-600 font-semibold">Precio</a>
          <a href="#faq" className="hidden sm:inline-flex px-3 h-10 items-center hover:text-orange-600 font-semibold">FAQ</a>
          <Link to="/login" className="px-3 h-10 inline-flex items-center hover:text-orange-600" data-testid="nav-login">Iniciar sesión</Link>
          <Link to="/register" className="px-4 h-10 inline-flex items-center btn-brand text-sm" data-testid="nav-register">Empezar gratis</Link>
        </nav>
      </div>
    </header>
  );
}

/* ---------------- Hero (with email capture) ---------------- */
function LeadForm({ compact = false, dataTestId = "lead-form" }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const { L } = useLegalInfo();

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) return toast.error("Debes aceptar la Política de Privacidad");
    if (!email || !email.includes("@")) {
      track("lead_submit_invalid", { source: compact ? "final-cta" : "hero" });
      return toast.error("Introduce un email válido");
    }
    setSubmitting(true);
    track("lead_submit_attempt", { source: compact ? "final-cta" : "hero" });
    try {
      await axios.post(`${API}/leads`, { email, source: compact ? "final-cta" : "hero", accept_privacy: true });
      track("lead_submit_success", { source: compact ? "final-cta" : "hero", email_domain: email.split("@")[1] });
      // Redirect to /register with email prefilled
      window.location.href = `/register?email=${encodeURIComponent(email)}`;
    } catch (err) {
      track("lead_submit_error", { source: compact ? "final-cta" : "hero" });
      toast.error(err?.response?.data?.detail || "Error enviando email");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={dataTestId} className={compact ? "max-w-lg" : "max-w-xl"}>
     <div className="flex flex-col sm:flex-row gap-2">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="tu@email.com"
        data-testid={`${dataTestId}-email`}
        className={`w-full sm:flex-1 ${compact ? "h-12" : "h-14"} px-4 border-2 border-slate-900 bg-white outline-none focus:border-orange-600 font-mono text-base`}
      />
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${dataTestId}-submit`}
        className={`${compact ? "h-12 px-6" : "h-14 px-8"} btn-brand inline-flex items-center justify-center gap-2 font-bold text-base disabled:opacity-60 whitespace-nowrap`}
      >
        {submitting ? "Un momento…" : <>Empezar 14 días gratis <ArrowRight className="w-5 h-5" /></>}
      </button>
     </div>
      <div className="mt-3 text-left">
        <PrivacyCheckbox checked={accepted} onChange={setAccepted} testid={`${dataTestId}-privacy`} />
        <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
          Responsable: {L("company_name").replace(/\.$/, "")}. Finalidad: enviarte información y crear tu cuenta. Derechos: acceso, rectificación y supresión en {L("email")}.
        </p>
      </div>
    </form>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ backgroundImage: "linear-gradient(#0001 1px, transparent 1px), linear-gradient(90deg, #0001 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      <div className="relative max-w-7xl mx-auto px-5 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="kbar mb-6 inline-flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse-soft" />
            <span>SaaS para autónomos · España</span>
          </div>
          <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-6xl lg:text-[4.5rem] leading-[0.95]">
            Presupuestos<br/>profesionales en<br/>
            <span className="inline-block relative">
              <span className="relative z-10 text-orange-600">30 segundos.</span>
              <span className="absolute bottom-1 left-0 right-0 h-3 bg-orange-200 -z-0" />
            </span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl">
            IA entrenada para 8 oficios. Describe el trabajo, genera PDF con tu logo y envíalo por WhatsApp o email. <span className="font-bold text-slate-900">Sin Excel, sin plantillas obsoletas.</span>
          </p>
          <div className="mt-8">
            <LeadForm dataTestId="hero-lead-form" />
          </div>
          <div className="mt-5 text-sm text-slate-500 flex items-center gap-5 flex-wrap">
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Sin tarjeta</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Cancela cuando quieras</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Desde 29 €/mes</span>
          </div>
        </div>

        <div className="lg:col-span-5">
          <InteractiveDemo />
        </div>
      </div>
    </section>
  );
}

/* ---------------- Social proof ---------------- */
function SocialProof() {
  const stats = [
    { value: "+1.200", label: "autónomos usan AutoPresupuestos" },
    { value: "+34.000", label: "presupuestos generados" },
    { value: "9s", label: "media de generación con IA" },
    { value: "4.9/5", label: "valoración en Google" },
  ];
  return (
    <section className="bg-slate-900 text-white border-y border-slate-900" data-testid="social-proof">
      <div className="max-w-7xl mx-auto px-5 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
        {stats.map((s) => (
          <div key={s.label} className="text-center sm:text-left">
            <div className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-orange-400">{s.value}</div>
            <div className="text-xs sm:text-sm text-slate-300 mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Features ---------------- */
function Features() {
  const items = [
    { icon: Sparkles, title: "IA por oficio", desc: "Entrenada con precios y desgloses reales de reformas, pintura, fontanería, electricidad y más." },
    { icon: FileText, title: "PDF con tu marca", desc: "Logo, color corporativo, IBAN y firma. Listo para imprimir o enviar en 1 clic." },
    { icon: Send, title: "Envío 1-clic", desc: "Email o WhatsApp directo. Enlace público con firma digital desde el móvil del cliente." },
    { icon: Receipt, title: "Contabilidad Verifactu", desc: "Ingresos, gastos, OCR por foto, libro IVA trimestral y exportación al asesor." },
    { icon: Wallet, title: "Cobros Bizum/Stripe", desc: "Link de pago directo en la factura. Recordatorios automáticos configurables." },
    { icon: Smartphone, title: "Instalable como app", desc: "PWA con push notifications cuando el cliente ve, acepta o firma." },
  ];
  return (
    <section id="como-funciona" className="border-t border-slate-200 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 py-20 sm:py-24">
        <div className="max-w-2xl">
          <div className="kbar">Todo en una herramienta</div>
          <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-5xl mt-3">De la obra al cobro, sin salir de la app.</h2>
          <p className="text-slate-600 mt-4 text-lg">Seis funciones que los autónomos que pagan <span className="font-bold">Holded + Excel + Gmail</span> tienen repartidas en tres apps.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {items.map((f, i) => (
            <div key={i} className="bg-white p-8 hover:bg-orange-50 transition-colors group">
              <div className="h-10 w-10 bg-slate-900 text-white flex items-center justify-center group-hover:bg-orange-600 transition-colors">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-5 font-display font-extrabold text-xl">{f.title}</h3>
              <p className="mt-2 text-slate-600 text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Demo ("video") ---------------- */
function DemoVideo() {
  const steps = [
    { n: "01", t: "Describe el trabajo", d: "«Pintar piso 80 m², 2 manos, color claro»", time: "2 seg" },
    { n: "02", t: "La IA genera las líneas", d: "Preparación, lijado, pintura, limpieza. Con cantidades y precios.", time: "7 seg" },
    { n: "03", t: "Descarga PDF o envía", d: "Con tu logo y tu IBAN. Email, WhatsApp o enlace público firmable.", time: "1 seg" },
  ];
  return (
    <section className="border-t border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 py-20 sm:py-24">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="kbar">Así de rápido</div>
            <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-5xl mt-3">Presupuesto completo<br/>en tres pasos.</h2>
          </div>
          <div className="text-right">
            <div className="font-display font-extrabold text-6xl text-orange-600 tabular-nums">≈ 10s</div>
            <div className="text-xs text-slate-500 uppercase tracking-widest">de media en producción</div>
          </div>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.n} className="relative bg-white border-2 border-slate-900 p-6">
              <div className="font-display font-extrabold text-5xl tabular-nums text-orange-600">{s.n}</div>
              <h3 className="mt-3 font-display font-extrabold text-xl">{s.t}</h3>
              <p className="mt-2 text-slate-600 text-sm">{s.d}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                <Clock className="w-3 h-3" /> {s.time}
              </div>
              {i < steps.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-300" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Trades ---------------- */
function Trades() {
  return (
    <section className="border-t border-slate-200 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="kbar">Plantillas listas</div>
            <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-5xl mt-3">8 oficios. 1 herramienta.</h2>
          </div>
          <Link to="/presupuestos" className="h-11 px-5 inline-flex items-center border-2 border-slate-900 hover:bg-slate-900 hover:text-white font-bold text-sm transition-colors">
            Ver todos los oficios <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {TRADES.map((t) => (
            <Link
              key={t}
              onClick={() => track("trade_click", { trade: t.toLowerCase() })}
              to={`/presupuestos-${t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ñ/g, "n")}`}
              className="bg-white p-6 hover:bg-orange-50 transition-colors group"
            >
              <div className="text-xs text-slate-400 group-hover:text-orange-600">→</div>
              <div className="mt-3 font-display font-extrabold text-lg">{t}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Testimonials ---------------- */
function Testimonials() {
  const t = [
    {
      name: "Carlos M.",
      trade: "Reformas integrales · Madrid",
      text: "Antes tardaba 45 minutos en cada presupuesto. Ahora lo hago en 30 segundos desde el móvil mientras estoy midiendo la obra. Me ha cambiado la vida.",
      initials: "CM",
    },
    {
      name: "Lucía R.",
      trade: "Pintura decorativa · Valencia",
      text: "El PDF con mi logo queda brutal. Envío por WhatsApp y me aceptan al instante. Ya pagué la cuota del año en presupuestos ganados la primera semana.",
      initials: "LR",
    },
    {
      name: "Iván T.",
      trade: "Fontanería · Sevilla",
      text: "Lo de escanear tickets con la cámara me ahorra una hora a la semana. Y el libro de IVA trimestral se lo mando al asesor en CSV sin mirar nada.",
      initials: "IT",
    },
  ];
  return (
    <section className="border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-5 py-20 sm:py-24">
        <div className="kbar">Quién lo usa</div>
        <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-5xl mt-3 max-w-3xl">
          «Me ha cambiado la forma de trabajar.»
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.map((x, i) => (
            <div key={i} className="bg-white border border-slate-200 p-7 flex flex-col">
              <div className="flex gap-0.5 text-orange-500 mb-4">
                {[0, 1, 2, 3, 4].map((j) => <Star key={j} className="w-4 h-4 fill-orange-500" />)}
              </div>
              <p className="text-slate-800 leading-relaxed flex-1">"{x.text}"</p>
              <div className="mt-6 flex items-center gap-3 pt-5 border-t border-slate-100">
                <div className="h-11 w-11 bg-slate-900 text-white flex items-center justify-center font-black text-sm">{x.initials}</div>
                <div>
                  <div className="font-display font-bold text-sm">{x.name}</div>
                  <div className="text-xs text-slate-500">{x.trade}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing ---------------- */
function Pricing() {
  const proFeatures = [
    "Presupuestos ilimitados con IA",
    "Clientes ilimitados",
    "PDF profesional con tu logo",
    "Envío por email y WhatsApp",
    "Firma digital del cliente",
    "Plantillas y duplicar presupuestos",
    "PWA + notificaciones push",
  ];
  const proPlusExtras = [
    "Importar y exportar BC3 (FIEBDC-3)",
    "OCR con Claude Vision (facturas)",
    "Contabilidad: ingresos, gastos, libros",
    "Facturación electrónica Verifactu",
    "Análisis IA de precios obsoletos",
  ];
  return (
    <section id="precio" className="border-t border-slate-200 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-5 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="kbar text-orange-400">Precio simple</div>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-6xl mt-3">Elige tu plan</h2>
          <p className="text-slate-300 mt-5 text-lg leading-relaxed">
            14 días gratis en ambos planes. Sin permanencia. Cancela en un clic.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-800 border border-slate-800 max-w-4xl mx-auto">
          <div className="bg-white text-slate-900 p-8" data-testid="landing-plan-pro">
            <div className="kbar text-orange-600">Básico</div>
            <div className="flex items-baseline gap-2 mt-3">
              <div className="font-display font-extrabold text-6xl tabular-nums">29</div>
              <div className="text-2xl font-bold">€</div>
              <div className="text-slate-500 text-lg">/mes</div>
            </div>
            <p className="text-sm text-slate-600 mt-3">Para autónomos que quieren cerrar presupuestos rápido.</p>
            <ul className="mt-6 space-y-2.5 text-sm">
              {proFeatures.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" /> <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link to="/register" onClick={() => track("pricing_cta_click", { plan: "pro" })} data-testid="pricing-cta-pro" className="mt-7 inline-flex items-center justify-center h-12 w-full bg-slate-900 text-white font-bold hover:bg-slate-800">
              Empieza con Básico · 14 días gratis
            </Link>
          </div>

          <div className="bg-slate-950 text-white p-8 relative" data-testid="landing-plan-proplus">
            <span className="absolute top-4 right-4 px-2 py-0.5 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-widest">
              Más completo
            </span>
            <div className="kbar text-orange-400">PRO</div>
            <div className="flex items-baseline gap-2 mt-3">
              <div className="font-display font-extrabold text-6xl tabular-nums">39</div>
              <div className="text-2xl font-bold">€</div>
              <div className="text-slate-400 text-lg">/mes</div>
            </div>
            <p className="text-sm text-slate-400 mt-3">Para profesionales que necesitan contabilidad y BC3.</p>
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex items-start gap-2 text-slate-300">
                <Check className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> <span>Todo lo del plan Básico</span>
              </li>
              {proPlusExtras.map((b) => (
                <li key={b} className="flex items-start gap-2 text-orange-300 font-semibold">
                  <Check className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link to="/register" onClick={() => track("pricing_cta_click", { plan: "proplus" })} data-testid="pricing-cta-proplus" className="mt-7 inline-flex items-center justify-center h-12 w-full bg-orange-600 text-white font-bold hover:bg-orange-700">
              Empieza con PRO · 14 días gratis
            </Link>
          </div>
        </div>
        <div className="mt-6 text-center text-xs text-slate-400">Sin tarjeta para empezar · Configura en 2 minutos · Pago seguro con Stripe</div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function FAQ() {
  const [open, setOpen] = useState(0);
  const q = [
    {
      q: "¿Realmente funciona la IA con mi oficio?",
      a: "Sí. Entrenamos el modelo (Claude Sonnet 4.5) con desgloses profesionales reales de 8 oficios en España: reformas, pintura, electricidad, fontanería, carpintería, limpieza, jardinería e instalaciones. Puedes ajustar cualquier línea después.",
    },
    {
      q: "¿Cobra Hacienda por los recibos generados?",
      a: "No. Nosotros no somos intermediarios fiscales. Generamos presupuestos y facturas con estructura Verifactu (CSV, hash, número correlativo). Tú las emites y tu asesor las presenta.",
    },
    {
      q: "¿Qué pasa si cancelo después de los 14 días?",
      a: "Puedes exportar todos tus datos (CSV + ZIP con PDFs) en 1 clic. Nada se bloquea. No cobramos el siguiente mes.",
    },
    {
      q: "¿Funciona en el móvil?",
      a: "Sí. Es PWA instalable en iPhone/Android: se añade al escritorio y funciona como app nativa. Puedes escanear tickets con la cámara y recibir push notifications cuando el cliente vea o acepte tu presupuesto.",
    },
    {
      q: "¿Puedo configurar mi IVA/IGIC y mi IBAN?",
      a: "Sí. Soporte completo para IVA (21%), IGIC (7%), sin impuesto (0%), y tu IBAN aparece en el pie de cada PDF. Configurable por defecto y ajustable por presupuesto.",
    },
    {
      q: "¿Cobráis por SMS, firma digital o extras?",
      a: "Nada extra. El plan Básico (29 €/mes) incluye presupuestos IA ilimitados, clientes, PDF, firma digital, email/WhatsApp y PWA. El plan PRO (39 €/mes) añade BC3, OCR de tickets, contabilidad y facturación Verifactu.",
    },
  ];
  return (
    <section id="faq" className="border-t border-slate-200 bg-white">
      <div className="max-w-4xl mx-auto px-5 py-20 sm:py-24">
        <div className="kbar">Preguntas frecuentes</div>
        <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-5xl mt-3">Dudas resueltas en 30 segundos.</h2>
        <div className="mt-12 divide-y-2 divide-slate-900">
          {q.map((x, i) => (
            <div key={i} className="py-1">
              <button
                onClick={() => { const nowOpen = open === i ? -1 : i; setOpen(nowOpen); if (nowOpen === i) track("faq_open", { question: x.q }); }}
                data-testid={`faq-${i}`}
                className="w-full text-left py-5 flex items-center justify-between gap-4 hover:text-orange-600 transition-colors"
              >
                <span className="font-display font-bold text-base sm:text-lg">{x.q}</span>
                <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <p className="pb-5 text-slate-600 leading-relaxed animate-fade-in-up">{x.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Final CTA ---------------- */
function FinalCta() {
  return (
    <section className="border-t-2 border-slate-900 bg-orange-50 relative overflow-hidden">
      <div className="absolute -top-8 -right-8 text-[200px] sm:text-[280px] font-display font-extrabold text-orange-200 leading-none select-none pointer-events-none">
        14
      </div>
      <div className="relative max-w-4xl mx-auto px-5 py-20 sm:py-28 text-center">
        <div className="kbar">14 días gratis · sin tarjeta</div>
        <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-6xl lg:text-7xl mt-4">
          Deja de perder obras por<br/><span className="text-orange-600">tardar en presupuestar.</span>
        </h2>
        <p className="mt-6 text-lg text-slate-700 max-w-2xl mx-auto">
          Configura tu cuenta en 2 minutos. Genera tu primer presupuesto en 30 segundos. Envíalo por WhatsApp hoy mismo.
        </p>
        <div className="mt-10 flex justify-center">
          <LeadForm compact dataTestId="final-lead-form" />
        </div>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */
function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-400 py-16 border-t border-slate-900">
      <div className="max-w-7xl mx-auto px-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
          <div className="col-span-2 sm:col-span-1">
            <div className="font-display font-extrabold text-white text-xl">AutoPresupuestos<span className="text-orange-500">.</span></div>
            <p className="text-sm mt-3 text-slate-500">Presupuestos profesionales con IA para autónomos.</p>
          </div>
          <div>
            <div className="kbar text-slate-500 mb-3">Producto</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#como-funciona" className="hover:text-white">Funciones</a></li>
              <li><a href="#precio" className="hover:text-white">Precio</a></li>
              <li><Link to="/instalar" className="hover:text-white">📲 Instalar en móvil</Link></li>
              <li><Link to="/register" className="hover:text-white">Empezar gratis</Link></li>
              <li><Link to="/login" className="hover:text-white">Iniciar sesión</Link></li>
            </ul>
          </div>
          <div>
            <div className="kbar text-slate-500 mb-3">Oficios</div>
            <ul className="space-y-2 text-sm">
              {TRADES.slice(0, 4).map((t) => (
                <li key={t}>
                  <Link to={`/presupuestos-${t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ñ/g, "n")}`} className="hover:text-white">
                    Presupuestos {t.toLowerCase()}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="kbar text-slate-500 mb-3">Legal</div>
            <ul className="space-y-2 text-sm">
              <li><LegalLinks className="flex flex-col gap-2" /></li>
              <li><a href="mailto:hola@autopresupuestos.pro" className="hover:text-white">Contacto</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-slate-800 text-sm flex items-center justify-between flex-wrap gap-3">
          <span>© 2026 AutoPresupuestos Pro</span>
          <span className="text-slate-500">Hecho en España · para autónomos exigentes</span>
        </div>
      </div>
    </footer>
  );
}
